import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

interface CancellationRequest {
  id: number;
  reason: string | null;
  plan_name_snapshot: string | null;
  plan_tier_snapshot: string | null;
  billing_cycle_snapshot: string | null;
  subscription_ends_at: string | null;
  kiwify_customer_email: string | null;
  created_at: string;
  company_id: string;
  company_name: string;
  company_slug: string;
  subscription_status: string;
  cancel_at_period_end: boolean;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
}

interface CancellationRequestsResponse {
  success: boolean;
  data: {
    requests: CancellationRequest[];
    total: number;
    page: number;
    totalPages: number;
  };
}

const CancellationRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<CancellationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      if (search) params.append('search', search);

      const response = await api.get<CancellationRequestsResponse>(
        `/admin/cancellation-requests?${params}`
      );

      if (response.data.success) {
        setRequests(response.data.data.requests);
        setTotal(response.data.data.total);
        setTotalPages(response.data.data.totalPages);
      }
    } catch (error) {
      console.error('Erro ao carregar cancelamentos:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    loadRequests();
  }, [page, search, loadRequests]);

  const formatDate = (iso: string | null) => {
    if (!iso) return 'N/A';
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const subscriptionBadge = (status: string, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          Cancelará no fim do período
        </span>
      );
    }
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      trial: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      suspended: 'bg-yellow-100 text-yellow-800',
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          colors[status] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Solicitações de Cancelamento</h1>
        <p className="text-gray-600">
          Motivos coletados no app antes do cliente acessar a Kiwify. Total: {total}
        </p>
      </div>

      <div className="mb-6">
        <input
          type="text"
          placeholder="Buscar por empresa, usuário ou motivo..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {loading ? (
        <div className="text-center py-8">Carregando...</div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plano</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fim do período</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status atual</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">Motivo</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {requests.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                        Nenhuma solicitação encontrada.
                      </td>
                    </tr>
                  ) : (
                    requests.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50 align-top">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(r.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{r.company_name}</div>
                          <div className="text-xs text-gray-500">{r.company_slug}</div>
                          {r.kiwify_customer_email && (
                            <div className="text-xs text-gray-400">{r.kiwify_customer_email}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {r.user_name ? (
                            <div>
                              <div className="text-gray-900">{r.user_name}</div>
                              {r.user_email && <div className="text-xs text-gray-500">{r.user_email}</div>}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{r.plan_name_snapshot || '—'}</div>
                          {r.billing_cycle_snapshot && (
                            <div className="text-xs text-gray-500">
                              {r.billing_cycle_snapshot === 'monthly' ? 'Mensal' : 'Anual'}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {r.subscription_ends_at
                            ? new Date(r.subscription_ends_at).toLocaleDateString('pt-BR')
                            : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {subscriptionBadge(r.subscription_status, r.cancel_at_period_end)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {r.reason ? (
                            <p className="whitespace-pre-wrap break-words">{r.reason}</p>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Não informado</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Anterior
              </button>

              <span className="px-4 py-2 text-gray-700">
                Página {page} de {totalPages}
              </span>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CancellationRequestsPage;
